import type { PreAuthRouteDecision } from '@rapid-recovery-agency-inc/insightt-graphql';

/**
 * Non-secret authentication lifecycle types for the Dashboard SSO adoption feature (T014).
 *
 * These types back the Redux-visible slices of the entities in
 * `specs/2026-09-02-3888-dashboard-sso-adoption/data-model.md`. No secret/token value
 * (access token, ID token, refresh token, authorization code, `state`, PKCE verifier/challenge,
 * or provider claim) is ever represented here — the constitution and data-model.md §6 require
 * those to live only in private in-memory boundary state owned by the auth controller/boundary
 * (T016/T017), never in Redux.
 *
 * `emailInput`, `startedAt`, and `abortHandle` from data-model.md §3 (Pre-Authentication Attempt)
 * are intentionally excluded from the Redux-visible shape below: `emailInput` is
 * component/request-lifetime-only and never persisted or emitted, `startedAt` is a private
 * monotonic time used only to derive a duration bucket, and `abortHandle` is a non-serializable
 * runtime handle. All three are owned by the in-memory auth controller (T016), not Redux.
 */

/** Lifecycle of one pre-authentication route-decision attempt (data-model.md §3). */
export enum PreAuthAttemptStatus {
  IDLE = 'IDLE',
  VALIDATING = 'VALIDATING',
  DECIDING = 'DECIDING',
  DECIDED = 'DECIDED',
  CANCELLED = 'CANCELLED',
  FAILED = 'FAILED',
}

/** Lifecycle of the OIDC authentication flow bound to a callback (data-model.md §5). */
export enum AuthFlowStatus {
  PREPARED = 'PREPARED',
  REDIRECTED = 'REDIRECTED',
  PROCESSING = 'PROCESSING',
  SUCCEEDED = 'SUCCEEDED',
  REJECTED = 'REJECTED',
  CONSUMED = 'CONSUMED',
}

/** Lifecycle of the current Dashboard session (data-model.md §7). */
export enum DashboardSessionStatus {
  UNKNOWN = 'UNKNOWN',
  AUTHENTICATING = 'AUTHENTICATING',
  AUTHENTICATED = 'AUTHENTICATED',
  RENEWING = 'RENEWING',
  SIGNED_OUT = 'SIGNED_OUT',
  RECOVERABLE = 'RECOVERABLE',
}

/** Lifecycle of a single-flight silent-renewal episode (data-model.md §8). */
export enum RenewalEpisodeStatus {
  IDLE = 'IDLE',
  ATTEMPTING = 'ATTEMPTING',
  SUCCEEDED = 'SUCCEEDED',
  INTERACTIVE_REQUIRED = 'INTERACTIVE_REQUIRED',
}

/**
 * Lifecycle of a single-flight cleanup/logout transaction (data-model.md §9).
 * `IDLE` is a Dashboard-local addition (not in data-model.md's transition table) representing
 * "no cleanup transaction has ever been requested yet" — the necessary Redux initial value
 * before the first `REQUESTED → LOCAL_CLEARING → LOCAL_CLEARED → PROVIDER_ATTEMPTED → COMPLETE |
 * PARTIAL_LOGOUT` transaction begins.
 */
export enum CleanupTransactionStatus {
  IDLE = 'IDLE',
  REQUESTED = 'REQUESTED',
  LOCAL_CLEARING = 'LOCAL_CLEARING',
  LOCAL_CLEARED = 'LOCAL_CLEARED',
  PROVIDER_ATTEMPTED = 'PROVIDER_ATTEMPTED',
  COMPLETE = 'COMPLETE',
  PARTIAL_LOGOUT = 'PARTIAL_LOGOUT',
}

/**
 * Non-secret Redux-visible slice of the Pre-Authentication Attempt entity (data-model.md §3).
 * `generation` is the `operationGeneration` field: an increasing integer where only the latest
 * value may change navigation or UI (stale-result rejection).
 */
export interface AuthPreAuthAttemptState {
  readonly generation: number;
  readonly status: PreAuthAttemptStatus;
  readonly decision: PreAuthRouteDecision | null;
}

/**
 * Non-secret Redux-visible slice of the Dashboard Session entity (data-model.md §7).
 * `principal`, `companyBranchScope`, and `authOrigin` are intentionally excluded: they are
 * trusted references resolved and owned by Backend session/permission utilities and the auth
 * boundary (T017), never derived from or stored as Redux-visible provider claims.
 */
export interface AuthSessionState {
  readonly generation: number;
  readonly status: DashboardSessionStatus;
}

/** Non-secret Redux-visible slice of the Renewal Episode entity (data-model.md §8). */
export interface AuthRenewalState {
  readonly status: RenewalEpisodeStatus;
}

/** Non-secret Redux-visible slice of the Cleanup Transaction entity (data-model.md §9). */
export interface AuthCleanupState {
  readonly status: CleanupTransactionStatus;
}

/** Root shape of the `auth` Redux slice. No secret/token value is ever stored here. */
export interface AuthState {
  readonly preAuthAttempt: AuthPreAuthAttemptState;
  readonly session: AuthSessionState;
  readonly renewal: AuthRenewalState;
  readonly cleanup: AuthCleanupState;
}

/** Payload for `setPreAuthAttemptStatus` — generation-guarded to reject stale results. */
export interface SetPreAuthAttemptStatusPayload {
  readonly generation: number;
  readonly status: PreAuthAttemptStatus;
}

/** Payload for `setPreAuthAttemptDecision` — generation-guarded to reject stale results. */
export interface SetPreAuthAttemptDecisionPayload {
  readonly generation: number;
  readonly decision: PreAuthRouteDecision | null;
}

/** Payload for `setSessionStatus` — generation-guarded to reject stale results. */
export interface SetSessionStatusPayload {
  readonly generation: number;
  readonly status: DashboardSessionStatus;
}

/** Payload for `setRenewalStatus`. */
export interface SetRenewalStatusPayload {
  readonly status: RenewalEpisodeStatus;
}

/** Payload for `setCleanupStatus`. */
export interface SetCleanupStatusPayload {
  readonly status: CleanupTransactionStatus;
}
